/**
 * ModerationThreads - conversations a moderator is allowed to look at
 * --------------------------------------------------------------------
 * Two ways a conversation appears here, and only two:
 *
 *   1. Somebody reported it.
 *   2. The contact-details detector fired on a message in it - the
 *      "let's carry on over WhatsApp" pattern, which is exactly the thing
 *      nobody reports, because the person being talked round does not think
 *      anything is wrong.
 *
 * Everything else is out of bounds, and that is enforced on the server rather
 * than by leaving a link off this screen - see backend/routes/messages.js.
 * This screen cannot show you a conversation you are not entitled to open,
 * even if you have the id.
 *
 * WHY THE BANNER AT THE TOP OF A THREAD IS NOT DECORATION
 * Opening one of these means reading a private conversation between an adult
 * and a sixteen-year-old. The banner says so, and every open is written to the
 * audit log with the moderator's name against it. A moderator who is
 * accountable reads differently from one who is not.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, AlertTriangle, Phone, EyeOff, Eye, ChevronLeft, ShieldAlert, Flag,
} from 'lucide-react';

import { messageApi } from '../services/api';
import { dateTime, timeAgo } from '../utils/format';
import Loader from './Loader';
import EmptyState from './EmptyState';
import Badge from './Badge';
import { useI18n } from '../hooks/useI18n';

const REASON_LABELS = {
  inappropriate_behaviour: 'moderation.reasonInappropriate',
  made_me_uncomfortable: 'moderation.reasonUnsafe',
  unsafe_work: 'moderation.reasonUnsafeWork',
  did_not_pay: 'Did not pay',
  did_not_turn_up: 'moderation.reasonNoShow',
  fake_profile: 'moderation.reasonFake',
  abusive_review: 'moderation.reasonAbusiveReview',
  something_else: 'moderation.reasonOther',
};

/**
 * ---------------- ONE CONVERSATION ----------------
 *
 * Exported, because a report card opens this directly now. A moderator
 * reading a report should not have to go and find the conversation in a
 * separate list - it is the evidence for the thing they are deciding.
 *
 * NOTE the useI18n call below, which this went without for a while. It
 * calls t() a dozen times and sits in a file whose OTHER component
 * destructures t perfectly well, so check-wiring saw nothing wrong and
 * opening any reported conversation threw "t is not defined". Same shape
 * as windowProblem() in MyOffer.jsx. A file having t is not the same as
 * this function having it.
 */
/**
 * Takes EITHER a chore id or an offer id with the adult who asked.
 *
 * Two kinds of conversation reach this screen since 25 September 2026: the
 * messages about a job, and a question somebody put to a student about their
 * offer. The second is the one place an adult writes first, so being able to
 * read it when it is reported matters more rather than less.
 */
export function ThreadReader({ choreId, withStudent, offerId, withPoster, onBack }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(offerId
        ? await messageApi.readOfferThread(offerId, withPoster)
        : await messageApi.readThread(choreId, withStudent));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [choreId, withStudent, offerId, withPoster]);

  useEffect(() => { load(); }, [load]);

  const toggleHidden = async (message) => {
    setBusy(message._id);
    try {
      await messageApi.hideMessage(message._id, !message.hidden);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Loader label={t('moderation.openingConversation')} />;

  if (!data) {
    return (
      <div className="stack">
        <button type="button" className="btn btn-ghost btn-sm btn-inline" onClick={onBack}>
          <ChevronLeft size={16} /> {t('moderation.backToList')}
        </button>
        <div className="alert alert-error">{error || t('moderation.noConversation')}</div>
      </div>
    );
  }

  const { chore, offer, poster, student, why, messages, enquiry } = data;

  return (
    <div className="stack" style={{ gap: 'var(--space-md)' }}>
      <button type="button" className="btn btn-ghost btn-sm btn-inline" onClick={onBack}>
        <ChevronLeft size={16} /> {t('moderation.backToList')}
      </button>

      {/* Said plainly, every single time. */}
      <div className="alert alert-warning">
        <ShieldAlert size={20} aria-hidden="true" />
        <div className="text-sm">
          <strong>{t('moderation.readingPrivate')}</strong>{' '}
          Opening it has been recorded in the audit log against your name. Read what you need to
          answer the report, and no more.
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 6 }}>
          {offer ? t('moderation.offerConversation') : chore.title}
        </h3>
        <div className="card-meta">
          {offer ? (
            <>
              <span>{offer.city}</span>
              <span>{offer.gone ? t('moderation.offerGone') : t('moderation.offerStillUp')}</span>
            </>
          ) : (
            <>
              <span>{dateTime(chore.scheduledFor)}</span>
              <span>{chore.status}</span>
            </>
          )}
        </div>

        {/* A question asked before applying reads differently from the job's
            own conversation: the student had not agreed to work for this
            person, and may have been asking precisely because something
            felt off. */}
        {enquiry && (
          <p className="text-sm" style={{ marginTop: 'var(--space-xs)' }}>
            <strong>{t('moderation.questionNotBooked')}</strong>
          </p>
        )}

        <hr className="divider" />

        <div className="detail-list">
          <div className="detail-row">
            <span className="detail-label">{t('moderation.poster')}</span>
            <span>
              <Link to={`/users/${poster._id}`}>{poster.firstName} {poster.lastName}</Link>
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('moderation.student')}</span>
            <span>
              <Link to={`/students/${student._id}`}>{student.firstName} {student.lastName}</Link>
              {student.schoolYear ? ` · ${student.schoolYear}` : ''}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('misc.whyYouCanSee')}</span>
            <span className="text-sm">
              {why.reports.length > 0 && (
                <>
                  {why.reports.length} report{why.reports.length === 1 ? '' : 's'}
                  {' - '}
                  {why.reports.map((r) => (REASON_LABELS[r.reason] ? t(REASON_LABELS[r.reason]) : r.reason)).join(', ')}
                  {why.flagged > 0 && '; '}
                </>
              )}
              {why.flagged > 0 && `${why.flagged} message${why.flagged === 1 ? '' : 's'} sharing contact details`}
            </span>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* The conversation itself. Deliberately not styled as the chat screen:
          this is a transcript being reviewed, not somebody's messages app. */}
      <div className="card">
        <h3 style={{ marginBottom: 'var(--space-sm)' }}>
          The conversation ({messages.length})
        </h3>

        {messages.length === 0 ? (
          <p className="text-muted text-sm">{t('misc.nothingSent')}</p>
        ) : (
          <div className="stack" style={{ gap: 'var(--space-xs)' }}>
            {messages.map((m) => (
              <div
                key={m._id}
                className={`transcript-line${m.hidden ? ' is-hidden' : ''}`}
              >
                <div className="row-between" style={{ gap: 'var(--space-xs)' }}>
                  <span className="row" style={{ gap: 6 }}>
                    <strong className="text-sm">
                      {m.fromStudent ? student.firstName : poster.firstName}
                    </strong>
                    <Badge tone={m.fromStudent ? 'soft-green' : 'soft-blue'}>
                      {m.fromStudent ? t('moderation.fromStudent') : t('moderation.fromPoster')}
                    </Badge>
                    {m.hasContactDetails && (
                      <Badge tone="soft-amber" icon={Phone}>
                        {t('moderation.contactDetails')}
                      </Badge>
                    )}
                    {m.hidden && <Badge tone="grey" icon={EyeOff}>{t('moderation.hidden')}</Badge>}
                  </span>
                  <span className="text-xs text-muted">{dateTime(m.createdAt)}</span>
                </div>

                <p className="transcript-body">{m.body}</p>

                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-inline"
                  onClick={() => toggleHidden(m)}
                  disabled={busy === m._id}
                >
                  {m.hidden
                    ? <><Eye size={14} /> {t('moderation.putItBack')}</>
                    : <><EyeOff size={14} /> {t('moderation.hideFromBoth')}</>}
                </button>
              </div>
            ))}
          </div>
        )}

        <p className="hint" style={{ marginTop: 'var(--space-sm)' }}>
          {t('moderation.hidingNote')}
        </p>
      </div>
    </div>
  );
}

/**
 * ---------------- THE LIST ----------------
 *
 * Two props, both for living inside the reports page rather than in a tab
 * of its own:
 *
 *   onlyUnreported  drop anything that already has a report, because that
 *                   conversation is reachable from the report itself now.
 *                   What is left is the set that exists for one reason -
 *                   the contact-details detector fired and nobody
 *                   complained, which is the case nobody ever reports.
 *   onOpen          let the page own which conversation is being read, so
 *                   the reader appears once, in one place.
 *
 * With neither, it behaves exactly as it always did.
 */
export default function ModerationThreads({ onlyUnreported = false, onOpen = null }) {
  const { t } = useI18n();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    messageApi
      .flaggedThreads()
      .then((d) => setThreads(d.threads || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const shown = onlyUnreported
    ? threads.filter((th) => !th.reports?.length)
    : threads;

  /**
   * `onOpen` is how the reports page opens a conversation in its own layout,
   * and it only knows about chores. An offer conversation is opened here
   * instead - which is right, because there is no report card to open it
   * from: what flags one of these is a report on a message inside it.
   */
  const openThread = (thread) => {
    const which = thread.kind === 'offer'
      ? { offerId: thread.offer._id, withPoster: thread.poster?._id }
      : { choreId: thread.chore._id, withStudent: thread.withStudent };
    if (onOpen) onOpen(which);
    else setOpen(which);
  };

  if (open) {
    return (
      <ThreadReader
        choreId={open.choreId}
        withStudent={open.withStudent}
        offerId={open.offerId}
        withPoster={open.withPoster}
        onBack={() => setOpen(null)}
      />
    );
  }
  if (loading) return <Loader label={t('common.loadingConversations')} />;

  // Inside the reports page, say nothing at all when there is nothing to
  // say. An empty state here would be a second "nothing to look at" above
  // the one the report queue already draws.
  if (onlyUnreported && shown.length === 0) return null;

  return (
    <>
      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-md)' }}>
        {t('moderation.onlyThreadsBefore')} <strong>{t('moderation.reported')}</strong>
        {t('moderation.onlyThreadsMiddle')}{' '}
        <strong>{t('moderation.sharedContact')}</strong>{t('moderation.onlyThreadsAfter')}
      </p>

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>{error}</div>}

      {shown.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title={t('moderation.nothingToLookAt')}
          message={t('moderation.nothingToLookAtBody')}
        />
      ) : (
        <div className="stack" style={{ gap: 'var(--space-xs)' }}>
          {/* The loop variable is `thread`, NOT `t`. It used to be `t`, which
              shadows the translator - so every t(...) inside here would have
              called the thread object as a function. Section 10c of HANDOFF
              records this trap; this file was one more instance of it. */}
          {shown.map((thread) => (
            <button
              key={thread.kind === 'offer'
                ? `offer:${thread.offer._id}:${thread.poster?._id}`
                : `${thread.chore._id}:${thread.withStudent || ''}`}
              type="button"
              className={`card card-hover flagged-thread${thread.urgent ? ' is-urgent' : ''}`}
              onClick={() => openThread(thread)}
            >
              <div className="row-between" style={{ gap: 'var(--space-xs)' }}>
                <strong>
                  {thread.kind === 'offer' ? t('moderation.offerConversation') : thread.chore.title}
                </strong>
                <span className="text-xs text-muted">{timeAgo(thread.lastSignal)}</span>
              </div>

              <div className="text-sm text-muted" style={{ marginTop: 2 }}>
                {thread.poster?.firstName} {thread.poster?.lastName?.[0]}.{' '}
                {t('moderation.posterTag')}{' '}
                {thread.student?.firstName} {thread.student?.lastName?.[0]}.{' '}
                {t('moderation.studentTag')}
                {thread.student?.schoolYear ? `, ${thread.student.schoolYear}` : ''})
              </div>

              <div className="row" style={{ gap: 6, marginTop: 'var(--space-xs)' }}>
                {thread.enquiry && (
                  <Badge tone="soft-blue">{t('moderation.questionBadge')}</Badge>
                )}
                {thread.urgent && (
                  <Badge tone="red" icon={AlertTriangle}>
                    {t('moderation.urgentReport')}
                  </Badge>
                )}
                {thread.openReports > 0 && (
                  <Badge tone="amber" icon={Flag}>
                    {t('moderation.openReports', { count: thread.openReports })}
                  </Badge>
                )}
                {thread.reports.length > thread.openReports && (
                  <Badge tone="grey">
                    {t('moderation.handled', { count: thread.reports.length - thread.openReports })}
                  </Badge>
                )}
                {thread.flagged > 0 && (
                  <Badge tone="soft-amber" icon={Phone}>
                    {t('moderation.withContactDetails', { count: thread.flagged })}
                  </Badge>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
